import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useUserRoles } from '../../../hooks/useUserRole';
import { hasButtonPermission } from '../../../helpers/check-role-permission';

/**
 * RoleButton Component
 *
 * A button that respects role-based permissions.
 * It can hide or disable the button based on user roles and permissions.
 */

export const RoleButton = forwardRef(({
  children,
  requiredRoles,
  roleButtonType,
  roleComponent,
  roleIsIconBtn = false,
  disabled = false,
  tooltip,
  withTooltip,
  ...buttonProps
}, ref) => {
  const { roles } = useUserRoles();

  let hasPermission = true;
  let disableOnly = false;

  if (roleButtonType) {
    const result = hasButtonPermission(roles, roleButtonType, roleComponent);
    hasPermission = result.hasAccess;
    disableOnly = result.disableOnly;
  } else if (requiredRoles) {
    hasPermission = roles.some((role) => requiredRoles.includes(role));
  }

  // Hide if no access AND not in disable-only mode
  const shouldHide = !hasPermission && !disableOnly;
  if (shouldHide) return null;

  const button = (
    <Button ref={ref} {...buttonProps} disabled={disableOnly || disabled}>
      {children}
    </Button>
  );

  const iconBtn = (
    <IconButton ref={ref} {...buttonProps} disabled={disableOnly || disabled}>
      {children}
    </IconButton>
  );

  if (disableOnly) {
    return (
      <Tooltip title={tooltip || "You don't have permission to perform this action"}>
        <span
          style={{
            display: 'inline-flex',
            verticalAlign: 'middle',
            opacity: 0.5,
            cursor: 'not-allowed',
            width: 'auto',
            height: 'auto',
          }}
        >
          {roleIsIconBtn ? iconBtn : button}
        </span>
      </Tooltip>
    );
  }

  if (withTooltip) {
    return (
      <Tooltip title={withTooltip}>
        <span>
          {roleIsIconBtn ? iconBtn : button}
        </span>
      </Tooltip>
    )
  }

  return roleIsIconBtn ? iconBtn : button;
});

RoleButton.displayName = 'RoleButton';

RoleButton.propTypes = {
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  roleButtonType: PropTypes.oneOf(['ADD', 'EDIT', 'DELETE', 'SAVE', 'EMAIL', 'EXPORT', 'APPROVE']),
  roleComponent: PropTypes.string,
  roleIsIconBtn: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default RoleButton;
